import React, { useState, useEffect } from 'react';
import './KanbanBoard.css';

function getCurrentStageForOrder(order) {
  const orderStatus = String(order?.status || '').toLowerCase();

  if (orderStatus.includes('cancelled') || orderStatus.includes('canceled')) {
    return 'cancelled';
  }

  const workflowSteps = Array.isArray(order?.workflow_steps) ? order.workflow_steps : [];
  let lastCompletedStage = 'order_received';

  for (let i = 0; i < workflowSteps.length; i++) {
    const step = workflowSteps[i];
    const isCompleted = step.done || step.status === 'completed' || step.actual_date;

    if (isCompleted) {
      lastCompletedStage = step.key;
    } else {
      break;
    }
  }

  return lastCompletedStage;
}

export default function KanbanBoard({
  orders,
  workflowSteps,
  activeOrderCode,
  onOrderClick,
  onMarkStepDone,
  stepLoadingKey,
  syncLoading 
}) {
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [localLoadingKey, setLocalLoadingKey] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  // Clear local loading spinner when the parent finishes its API call
  useEffect(() => {
    if (!stepLoadingKey) {
      setLocalLoadingKey(null);
    }
  }, [stepLoadingKey]);

  // Unified function to handle both checkbox clicks and drops
  const handleMarkDone = (orderCode, targetStep) => {
    setLocalLoadingKey(`${orderCode}-${targetStep.key}`);
    onMarkStepDone(targetStep, orderCode); 
  };

  const handleCheckboxChange = (orderCode) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderCode)) {
        next.delete(orderCode);
      } else {
        next.add(orderCode);
      }
      return next;
    });
  };

  const ordersByStage = workflowSteps.reduce((acc, step) => {
    acc[step.key] = [];
    return acc;
  }, {});

  orders.forEach((order) => {
    const stage = getCurrentStageForOrder(order);
    if (ordersByStage[stage]) {
      ordersByStage[stage].push(order);
    }
  });

  const handleCardKeyDown = (event, orderCode) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOrderClick(orderCode);
    }
  };

  return (
    <div className="kanban-container-vertical">
      <div className="kanban-board-vertical">
        {workflowSteps.map((step, columnIndex) => {
          // Drag Logic: Only allow dropping into the IMMEDIATE next step
          const isNextStep = draggedOrder && columnIndex === draggedOrder.sourceStepIndex + 1 && !step.auto;

          return (
            <div 
              key={step.key} 
              className="kanban-column-vertical"
              onDragOver={(e) => {
                if (isNextStep) {
                  e.preventDefault(); 
                  e.currentTarget.classList.add('drag-over');
                }
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('drag-over');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                if (isNextStep && draggedOrder) {
                  // Mark the TARGET step column as done so the card moves here
                  handleMarkDone(draggedOrder.orderCode, step);
                }
              }}
            >
              <div className="kanban-column-header-vertical">
                <h3>{step.label}</h3>
                <span className="kanban-count-vertical">
                  {syncLoading ? 0 : (ordersByStage[step.key]?.length || 0)}
                </span>
              </div>

              <div className="kanban-cards-area-vertical">
                {/* 1. SKELETON LOADING STATE */}
                {syncLoading ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <div key={`skeleton-${idx}`} className="kanban-card-vertical skeleton-card">
                      <div className="skeleton-checkbox"></div>
                      <div className="kanban-card-content-vertical">
                        <div className="skeleton-text"></div>
                        <div className="skeleton-text short"></div>
                      </div>
                    </div>
                  ))
                ) 
                /* 2. ACTUAL DATA STATE */
                : ordersByStage[step.key]?.length > 0 ? (
                  (ordersByStage[step.key] || []).map((order) => {
                    const orderStep = order.workflow_steps.find((s) => s.key === step.key);

// Identify the immediate next step so the checkbox can trigger it
const nextStep = columnIndex < workflowSteps.length - 1 ? workflowSteps[columnIndex + 1] : null;
const canMarkNext = nextStep && nextStep.backendKey && !nextStep.auto;

// 👇 NEW DELAY LOGIC 👇
let isDelayed = orderStep?.delay > 0; // Keep existing backend delay logic as a fallback
if (!isDelayed && nextStep) {
  const nextOrderStep = order.workflow_steps.find((s) => s.key === nextStep.key);
  
  if (nextOrderStep && nextOrderStep.planned_date) {
    let dateString = nextOrderStep.planned_date;
    
    // 1. Ensure the date string uses the standard 'T' separator instead of a space
    if (typeof dateString === 'string') {
      dateString = dateString.replace(' ', 'T');
      
      // 2. Force Indian Standard Time (IST) if the database didn't provide a timezone
      // This stops JS from accidentally treating it as a UTC date.
      if (!dateString.includes('Z') && !dateString.includes('+')) {
        dateString += '+05:30';
      }
    }

    // Convert both to raw timestamps (milliseconds) for strict exact-minute comparison
    const plannedTimestamp = new Date(dateString).getTime();
    const currentTimestamp = Date.now();

    // If it is a valid date AND the current exact time is past the planned time
    if (!isNaN(plannedTimestamp) && currentTimestamp > plannedTimestamp) {
      isDelayed = true;
    }
  }
}


const isLoading = localLoadingKey === `${order.order_code}-${nextStep?.key}` || stepLoadingKey === nextStep?.key;
                    const isDragging = draggedOrder?.orderCode === order.order_code;
                    const isSelected = selectedOrders.has(order.order_code);

                    return (
                      <div
                        key={order.order_code}
                        className={`kanban-card-vertical ${
                          activeOrderCode === order.order_code ? 'active' : ''
                        } ${orderStep?.done ? 'completed' : ''} ${isDragging ? 'dragging' : ''} ${
                          isSelected ? 'selected' : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        draggable={!isLoading && canMarkNext} 
                        onDragStart={(e) => {
                          setDraggedOrder({ 
                            orderCode: order.order_code, 
                            sourceStepIndex: columnIndex, 
                            sourceStep: step 
                          });
                          e.dataTransfer.setData('text/plain', order.order_code);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => {
                          setDraggedOrder(null);
                        }}
                        onClick={() => onOrderClick(order.order_code)}
                        onKeyDown={(e) => handleCardKeyDown(e, order.order_code)}
                        title={`${order.order_code}\n${order.product_name}\n${order.channel}`}
                      >
                        {/* Checkbox marks the NEXT step as done */}
                        {canMarkNext && (
                          <input
                            type="checkbox"
                            className="kanban-checkbox"
                            checked={false} 
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleMarkDone(order.order_code, nextStep);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isLoading}
                            aria-label={`Mark ${nextStep.label} as done`}
                            title={`Mark next step: ${nextStep.label} as done`}
                          />
                        )}

                        <div className="kanban-card-content-vertical">
                          <span className="order-code-vertical">{order.order_code}</span>
                          {isDelayed && (
                            <span className="delay-indicator">⚠ Delayed</span>
                          )}
                        </div>

                        {/* Spinner Overlay */}
                        {isLoading && (
                          <div className="kanban-loading-overlay-vertical">
                            <div className="spinner-vertical" />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) 
                /* 3. EMPTY STATE */
                : (
                  <div className="kanban-empty-vertical">—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}